import { useGetMe, type MeOut, type Role } from '@myrentalmate/api-client';

export type Me = MeOut;

/** The signed-in user, their active org and role. Unwraps orval's { data, status } envelope. */
export function useMe() {
  return useGetMe({ query: { select: (response) => response.data } });
}

export const ROLE_LABELS: Record<Role, string> = {
  org_admin: 'Property manager (admin)',
  property_manager: 'Property manager',
  leasing_agent: 'Leasing agent',
  accountant: 'Accountant',
  maintenance_tech: 'Maintenance tech',
  read_only: 'Read only',
  resident: 'Resident',
  owner: 'Owner',
  applicant: 'Applicant',
};

/** Which area of the app a role lands in. */
export function homePathFor(role: Role): string {
  switch (role) {
    case 'resident':
      return '/portal';
    case 'owner':
      return '/owner';
    case 'applicant':
      return '/apply';
    default:
      return '/staff';
  }
}

/** What to call this person on screen. */
export function displayName(me: Me): string {
  return me.display_name || me.email;
}

/** The address of the business's logo. The version makes a new logo show at once. */
export function orgLogoUrl(me: Pick<Me, 'org_logo_version'>): string | null {
  return me.org_logo_version ? `/api/orgs/current/logo?v=${me.org_logo_version}` : null;
}

/** "SM" for Sam Morgan, from first and last name. A one-word name or an email gives one letter. */
export function initialsOf(me: Pick<Me, 'display_name' | 'email'>): string {
  const words = me.display_name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return me.email.charAt(0).toUpperCase();
  const first = words[0].charAt(0);
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : '';
  return `${first}${last}`.toUpperCase();
}

/** The business whose records this person is looking at. */
export function activeOrgName(me: Me): string | undefined {
  return (
    me.support?.org_name ?? me.memberships.find((m) => m.org_id === me.active_org_id)?.org_name
  );
}
